import React, { createContext, useContext, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../services/supabaseClient';
import { Organization } from '../types';
import { useAuth } from './AuthContext';
import { organizationService } from '../services/organizationService';

interface OrganizationContextType {
    currentOrganization: Organization | null;
    currentRole: string | null;
    organizations: Organization[];
    isLoading: boolean;
    selectOrganization: (orgId: string) => void;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const OrganizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, isLoading: authIsLoading } = useAuth();

    // Derived local state for the UI with cache fallback
    const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(() => {
        try {
            const cached = localStorage.getItem('cached_current_organization');
            return cached ? JSON.parse(cached) : null;
        } catch {
            return null;
        }
    });
    const [currentRole, setCurrentRole] = useState<string | null>(() => {
        return localStorage.getItem('userRole') || null;
    });
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [safetyTimeoutTriggered, setSafetyTimeoutTriggered] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setSafetyTimeoutTriggered(true);
        }, 3500);
        return () => clearTimeout(timer);
    }, []);

    // React Query declarative fetch: Only runs when auth is definitely finished and user exists
    const { data: memberData, isLoading: queryIsLoading } = useQuery({
        queryKey: ['userOrganizations', user?.id, user?.email],
        queryFn: async () => {
            console.log('[OrgContext] React Query fetching for user:', user?.id, user?.email);

            // 1. Fetch memberships from organization_members
            const membersPromise = supabase
                .from('organization_members')
                .select(`
                    role,
                    organization_id,
                    organization:organizations(*)
                `)
                .eq('user_id', user?.id);

            // 2. Fetch organizations where user is owner by email
            const ownedPromise = user?.email
                ? supabase
                    .from('organizations')
                    .select('*')
                    .ilike('owner_email', user.email)
                : Promise.resolve({ data: [] as any[], error: null });

            const [membersRes, ownedRes] = await Promise.all([membersPromise, ownedPromise]);

            if (membersRes.error) {
                console.warn('[OrgContext] Fetch members error:', membersRes.error);
            }
            if (ownedRes.error) {
                console.warn('[OrgContext] Fetch owned orgs error:', ownedRes.error);
            }

            const members = (membersRes.data || []) as any[];
            const ownedOrgs = (ownedRes.data || []) as any[];

            // Merge owned orgs if not already present in members
            const existingOrgIds = new Set(members.map((m: any) => m.organization_id || m.organization?.id));

            for (const org of ownedOrgs) {
                if (org?.id && !existingOrgIds.has(org.id)) {
                    members.push({
                        role: 'owner',
                        organization_id: org.id,
                        organization: org
                    });
                    existingOrgIds.add(org.id);
                }
            }

            return members;
        },
        enabled: !authIsLoading && !!user?.id,
        staleTime: 1000 * 60 * 5, // Cache for 5 minutes to prevent flickering
        retry: 2
    });

    // We are loading only if Auth is loading, OR if the Query is running and we don't even have a cached organization yet
    const isLoading = !safetyTimeoutTriggered && (authIsLoading || (queryIsLoading && !!user?.id && !currentOrganization));

    // Sync derived state when Query data changes
    useEffect(() => {
        if (!user?.id) {
            setOrganizations([]);
            setCurrentOrganization(null);
            setCurrentRole(null);
            localStorage.removeItem('cached_current_organization');
            return;
        }

        if (memberData) {
            const orgs = memberData.map((m: any) => m.organization).filter(Boolean) as Organization[];
            setOrganizations(orgs);
            console.log('[OrgContext] Synced orgs from query:', orgs.length);

            // Auto-select
            const storedOrgId = localStorage.getItem('selectedOrganizationId');
            let selectedOrg: Organization | null = null;

            if (storedOrgId) {
                selectedOrg = orgs.find(o => o.id === storedOrgId) || null;
            }
            if (!selectedOrg && orgs.length > 0) {
                selectedOrg = orgs[0];
            }

            if (selectedOrg) {
                setCurrentOrganization(selectedOrg);
                localStorage.setItem('selectedOrganizationId', selectedOrg.id);
                localStorage.setItem('cached_current_organization', JSON.stringify(selectedOrg));
                const mem = memberData.find((m: any) => m.organization_id === selectedOrg?.id);
                if (mem) {
                    setCurrentRole(mem.role);
                }
            }
        }
    }, [memberData, user?.id]);

    // Inject CSS variables for deep White-Label customization
    useEffect(() => {
        if (currentOrganization) {
            const root = document.documentElement;

            const hexToRgb = (hex: string) => {
                const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
                return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : null;
            };

            const primary = currentOrganization.primary_color || 'var(--primary-color, #a855f7)';
            const secondary = currentOrganization.secondary_color || 'var(--secondary-color, #7c3aed)';

            // Provide a graceful fallback to original TrazAPP styling
            root.style.setProperty('--primary-color', primary);
            root.style.setProperty('--secondary-color', secondary);
            root.style.setProperty('--primary-color-rgb', hexToRgb(primary) || 'var(--primary-color-rgb, 168, 85, 247)');
            root.style.setProperty('--secondary-color-rgb', hexToRgb(secondary) || 'var(--secondary-color-rgb, 124, 58, 237)');

            // ---- DYNAMIC PWA MANIFEST HOMEPAGE ----
            let manifestLink = document.querySelector('link[rel="manifest"]');
            if (manifestLink) {
                const manifestParams = {
                    short_name: currentOrganization.name,
                    name: `Portal - ${currentOrganization.name}`,
                    icons: [
                        {
                            src: currentOrganization.logo_url || "/apple-touch-icon.png",
                            sizes: "192x192 512x512",
                            type: "image/png"
                        }
                    ],
                    start_url: `/${currentOrganization.slug}/portal`,
                    display: "standalone",
                    theme_color: primary,
                    background_color: "#020617"
                };

                const blob = new Blob([JSON.stringify(manifestParams)], { type: 'application/json' });
                manifestLink.setAttribute('href', URL.createObjectURL(blob));
            }

            let themeMeta = document.querySelector('meta[name="theme-color"]');
            if (themeMeta) {
                themeMeta.setAttribute('content', primary);
            }
            // ---------------------------------------
        }
    }, [currentOrganization]);

    const selectOrganization = (orgId: string) => {
        const org = organizations.find(o => o.id === orgId);
        if (org) {
            setCurrentOrganization(org);
            localStorage.setItem('selectedOrganizationId', org.id);
            localStorage.setItem('cached_current_organization', JSON.stringify(org));

            // Re-fetch role for new context instantly from cached query data
            const mem = memberData?.find((m: any) => m.organization_id === org.id);
            if (mem) {
                setCurrentRole(mem.role);
            } else {
                // Fallback direct request
                supabase
                    .from('organization_members')
                    .select('role')
                    .eq('organization_id', org.id)
                    .eq('user_id', user?.id)
                    .single()
                    .then(({ data }) => {
                        if (data) setCurrentRole(data.role);
                    });
            }
        }
    };

    return (
        <OrganizationContext.Provider value={{ currentOrganization, currentRole, organizations, isLoading, selectOrganization }}>
            {children}
        </OrganizationContext.Provider>
    );
};

export const useOrganization = () => {
    const context = useContext(OrganizationContext);
    if (context === undefined) {
        throw new Error('useOrganization must be used within an OrganizationProvider');
    }
    return context;
};
